import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layout/Layout';

interface TagCategory {
  id: number;
  name: string;
  created_at: string;
  tag_count: number;
}

interface Tag {
  id: number;
  name: string;
  tag_category_id: number;
  created_at: string;
  category: {
    id: number;
    name: string;
  };
}

const Tags: React.FC = () => {
  const [categories, setCategories] = useState<TagCategory[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<TagCategory | null>(null);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingTags, setLoadingTags] = useState(false);

  // Category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    id: null as number | null,
    name: ''
  });
  const [submittingCategory, setSubmittingCategory] = useState(false);

  // Tag modal
  const [showTagModal, setShowTagModal] = useState(false);
  const [tagForm, setTagForm] = useState({
    id: null as number | null,
    name: '',
    tag_category_id: null as number | null
  });
  const [submittingTag, setSubmittingTag] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get('/admin/app-assets/tag-categories');
      if (response.data.success) {
        setCategories(response.data.data.tag_categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchTags = async (categoryId: number) => {
    try {
      setLoadingTags(true);
      const response = await axios.get(`/admin/app-assets/tags?categoryId=${categoryId}`);
      if (response.data.success) {
        setTags(response.data.data.tags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const selectCategory = (category: TagCategory) => {
    setSelectedCategory(category);
    fetchTags(category.id);
  };

  // Category CRUD
  const openCategoryModal = (category?: TagCategory) => {
    if (category) {
      setCategoryForm({ id: category.id, name: category.name });
    } else {
      setCategoryForm({ id: null, name: '' });
    }
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setCategoryForm({ id: null, name: '' });
  };

  const handleCategorySubmit = async () => {
    if (!categoryForm.name.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      setSubmittingCategory(true);
      if (categoryForm.id) {
        await axios.put(`/admin/app-assets/tag-categories/${categoryForm.id}`, {
          name: categoryForm.name
        });
        alert('Category updated successfully!');
      } else {
        await axios.post('/admin/app-assets/tag-categories', {
          name: categoryForm.name
        });
        alert('Category created successfully!');
      }
      closeCategoryModal();
      await fetchCategories();
    } catch (error: any) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.message || 'Failed to save category');
    } finally {
      setSubmittingCategory(false);
    }
  };

  const handleCategoryDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/admin/app-assets/tag-categories/${id}`);
      alert('Category deleted successfully!');
      if (selectedCategory?.id === id) {
        setSelectedCategory(null);
        setTags([]);
      }
      await fetchCategories();
    } catch (error: any) {
      console.error('Error deleting category:', error);
      alert(error.response?.data?.message || 'Failed to delete category');
    }
  };

  // Tag CRUD
  const openTagModal = (tag?: Tag) => {
    if (tag) {
      setTagForm({ 
        id: tag.id, 
        name: tag.name,
        tag_category_id: tag.tag_category_id
      });
    } else {
      setTagForm({ 
        id: null, 
        name: '',
        tag_category_id: selectedCategory?.id || null
      });
    }
    setShowTagModal(true);
  };

  const closeTagModal = () => {
    setShowTagModal(false);
    setTagForm({ id: null, name: '', tag_category_id: null });
  };

  const handleTagSubmit = async () => {
    if (!tagForm.name.trim()) {
      alert('Please enter a tag name');
      return;
    }

    if (!tagForm.tag_category_id) {
      alert('Please select a category');
      return;
    }

    try {
      setSubmittingTag(true);
      if (tagForm.id) {
        await axios.put(`/admin/app-assets/tags/${tagForm.id}`, {
          name: tagForm.name,
          tag_category_id: tagForm.tag_category_id
        });
        alert('Tag updated successfully!');
      } else {
        await axios.post('/admin/app-assets/tags', {
          name: tagForm.name,
          tag_category_id: tagForm.tag_category_id
        });
        alert('Tag created successfully!');
      }
      closeTagModal();
      if (selectedCategory) {
        await fetchTags(selectedCategory.id);
      }
      await fetchCategories();
    } catch (error: any) {
      console.error('Error saving tag:', error);
      alert(error.response?.data?.message || 'Failed to save tag');
    } finally {
      setSubmittingTag(false);
    }
  };

  const handleTagDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/admin/app-assets/tags/${id}`);
      alert('Tag deleted successfully!');
      if (selectedCategory) {
        await fetchTags(selectedCategory.id);
      }
      await fetchCategories();
    } catch (error: any) {
      console.error('Error deleting tag:', error);
      alert(error.response?.data?.message || 'Failed to delete tag');
    }
  };

  return (
    <Layout>
      <div>
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Tags</h4>
                <p className="text-muted mb-0">Manage user tags and categories</p>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Tag Categories - Left Side */}
          <div className="col-md-4">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    <i className="bx bx-category me-2"></i>
                    Categories
                  </h5>
                  <button className="btn btn-sm btn-primary" onClick={() => openCategoryModal()}>
                    <i className="bx bx-plus"></i>
                  </button>
                </div>

                {loadingCategories ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-category font-size-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No categories found</p>
                  </div>
                ) : (
                  <div className="list-group">
                    {categories.map(category => (
                      <div
                        key={category.id}
                        className={`list-group-item list-group-item-action ${selectedCategory?.id === category.id ? 'active' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => selectCategory(category)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-semibold">{category.name}</div>
                            <small className={selectedCategory?.id === category.id ? 'text-white-50' : 'text-muted'}>
                              {category.tag_count} tags
                            </small>
                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn btn-sm btn-warning me-1"
                              onClick={() => openCategoryModal(category)}
                            >
                              <i className="bx bx-edit"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleCategoryDelete(category.id, category.name)}
                            >
                              <i className="bx bx-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tags - Right Side */}
          <div className="col-md-8">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    <i className="bx bx-purchase-tag me-2"></i>
                    Tags
                    {selectedCategory && (
                      <span className="badge bg-primary ms-2">{selectedCategory.name}</span>
                    )}
                  </h5>
                  {selectedCategory && (
                    <button className="btn btn-sm btn-primary" onClick={() => openTagModal()}>
                      <i className="bx bx-plus me-1"></i>
                      Add Tag
                    </button>
                  )}
                </div>

                {!selectedCategory ? (
                  <div className="text-center py-5">
                    <i className="bx bx-category-alt font-size-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">Select a category to view tags</p>
                  </div>
                ) : loadingTags ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : tags.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-purchase-tag font-size-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No tags in this category</p>
                    <button className="btn btn-primary mt-2" onClick={() => openTagModal()}>
                      <i className="bx bx-plus me-1"></i>
                      Add First Tag
                    </button>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Created Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tags.map(tag => (
                          <tr key={tag.id}>
                            <td>{tag.id}</td>
                            <td className="fw-semibold">{tag.name}</td>
                            <td>
                              <span className="badge bg-info">{tag.category.name}</span>
                            </td>
                            <td>
                              <small>{new Date(tag.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-warning me-2"
                                onClick={() => openTagModal(tag)}
                              >
                                <i className="bx bx-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleTagDelete(tag.id, tag.name)}
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Category Modal */}
        {showCategoryModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {categoryForm.id ? 'Edit Category' : 'Add New Category'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeCategoryModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Category Name *</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter category name"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeCategoryModal}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleCategorySubmit}
                    disabled={submittingCategory}
                  >
                    {submittingCategory ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-save me-1"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tag Modal */}
        {showTagModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {tagForm.id ? 'Edit Tag' : 'Add New Tag'}
                  </h5>
                  <button type="button" className="btn-close" onClick={closeTagModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tag Name *</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={tagForm.name}
                      onChange={(e) => setTagForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter tag name"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Category *</label>
                    <select 
                      className="form-select"
                      value={tagForm.tag_category_id || ''}
                      onChange={(e) => setTagForm(prev => ({ ...prev, tag_category_id: parseInt(e.target.value) }))}
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeTagModal}>
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleTagSubmit}
                    disabled={submittingTag}
                  >
                    {submittingTag ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-save me-1"></i>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Tags;
